import { useRef, type KeyboardEvent } from 'react';

interface OptionCardsProps {
  options: string[];
  value: string | undefined;
  onChange: (value: string) => void;
  compact?: boolean;
  name: string;
}

// Tarjetas de opción única (radiogroup con roving tabindex). Toda la tarjeta
// es clicable; flechas mueven, Enter/Espacio selecciona (handoff §10).
export function OptionCards({ options, value, onChange, compact, name }: OptionCardsProps) {
  const refs = useRef<Array<HTMLDivElement | null>>([]);
  const selectedIdx = options.indexOf(value ?? '');

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>, idx: number) => {
    let next: number | null = null;
    if (e.key === 'ArrowDown' || e.key === 'ArrowRight') next = (idx + 1) % options.length;
    if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') next = (idx - 1 + options.length) % options.length;
    if (next !== null) {
      e.preventDefault();
      onChange(options[next]!);
      refs.current[next]?.focus();
    }
    if (e.key === ' ' || e.key === 'Enter') {
      e.preventDefault();
      onChange(options[idx]!);
    }
  };

  return (
    <div
      className={'gb-options' + (compact ? ' gb-options--compact' : '')}
      role="radiogroup"
      aria-label={name}
    >
      {options.map((opt, idx) => {
        const selected = value === opt;
        const tabbable = selected || (selectedIdx === -1 && idx === 0);
        return (
          <div
            key={opt}
            ref={(el) => {
              refs.current[idx] = el;
            }}
            role="radio"
            aria-checked={selected}
            tabIndex={tabbable ? 0 : -1}
            className={'gb-option' + (selected ? ' is-selected' : '')}
            onClick={() => onChange(opt)}
            onKeyDown={(e) => onKeyDown(e, idx)}
          >
            <span className="gb-option__mark" aria-hidden="true">
              {selected ? '✓' : ''}
            </span>
            <span className="gb-option__text">{opt}</span>
          </div>
        );
      })}
    </div>
  );
}
